import { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Result } from "antd";
import { t } from "i18next";
import { Link, Spinner } from "../components/common";
import AppLayout from "../layout";
import { setCurrentApp } from "../store/appSlice";
import getPrivilege from "../utils/privileges/privileges";
import { getAppFromPath, getCurrentApp } from "./appRegistry";
import { configs } from "../configs";
import { getDashboardRedirectPath } from "helpers/getDashboardRedirectPath";

const DirectRouter = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const privileges = useSelector((state) => state.app.privileges);
  const [fmsRoutes, setFmsRoutes] = useState([]);
  const [rmsRoutes, setRmsRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const user = useSelector((state) => state.app?.user?.info)

  useEffect(() => {
    const loadAllRoutes = async () => {
      try {
        const fmsModule = await import("../apps/fms/routes");
        const fmsAppRoutes = fmsModule.default || fmsModule.APP_ROUTES;
        const rmsModule = await import("../apps/rms/routes");
        const rmsAppRoutes = rmsModule.default || rmsModule.APP_ROUTES;
        setFmsRoutes(fmsAppRoutes);
        setRmsRoutes(rmsAppRoutes);
        const currentApp = getAppFromPath(location.pathname) || getCurrentApp();
        if (currentApp) {
          dispatch(setCurrentApp(currentApp.key));
        }
      } catch (error) {
        console.error("Failed to load routes:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllRoutes();
  }, [location.pathname]);

  const getAvailableRoutes = (routes) => {
    if (!routes || !privileges) return [];
    return routes.filter((route) =>
      getPrivilege(route.privilege, privileges) || route.privilege === true
    );
  };

  const availableFmsRoutes = getAvailableRoutes(fmsRoutes);
  const availableRmsRoutes = getAvailableRoutes(rmsRoutes);

  if (isLoading) {
    return <Spinner size="large" fullPageSpinner />
  }

  if (!privileges) {
    return <Spinner size="large" fullPageSpinner />
  }

  const defaultLang = configs.languages[0]?.key || "en";
  
  const LanguageRedirect = ({ to }) => {
    const { lang } = useParams();
    const currentLang = lang || defaultLang;
    return <Navigate to={`/${currentLang}${to}`} replace />;
  };

  // Use route parameters instead of generating routes for each language

  const renderRoute = ({ Component, path, name }, appKey) => (
    <Route
      key={`${appKey}-${name}`}
      path={`/:lang/${appKey}${path}`}
      element={
        <AppLayout>
          <Suspense fallback={<Spinner size="large" fullPageSpinner />}>
            <Component />
          </Suspense>
        </AppLayout>
      }
    />
  );

  return (
    <Routes>
      <Route
        path="/"
        element={<Navigate to={getDashboardRedirectPath(user)} replace />}
      />

      <Route path="/:lang" element={<LanguageRedirect to="/fms/dashboard" />} />
      {availableFmsRoutes.map((route) => renderRoute(route, "fms"))}
      {availableRmsRoutes.map((route) => renderRoute(route, "rms"))}

      <Route
        path="/:lang/fms"
        element={<LanguageRedirect to="/fms/dashboard" />}
      />
      <Route
        path="/:lang/rms"
        element={<LanguageRedirect to="/rms/dashboard" />}
      />

      <Route
        path="*"
        element={
          <div className="d-grid" style={{ height: "100vh", placeContent: "center" }}>
            <Result
              status="404"
              title="404"
              subTitle={t("Sorry, the page you visited does not exist.")}
              extra={<Link to="/">{t("Back to Home")}</Link>}
            />
          </div>
        }
      />
    </Routes>
  )
};

export default DirectRouter;
